import * as React from 'react';

import {
  createStyles,
  Grid,
  Paper,
  Theme,
  Typography,
  WithStyles,
  withStyles
} from '@material-ui/core';
import { ArrowDropDownRounded, ArrowDropUpRounded } from '@material-ui/icons';

import classnames from 'classnames';

import { green, red } from '@material-ui/core/colors';
import numeral from 'numeral';
import InfoBubble from './InfoBubble';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(4),
      position: 'relative'
    },
    value: {
      fontWeight: 'bold',
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1)
    },
    title: {
      lineHeight: 'inherit',
      width: '100%'
    },
    comparison: {
      position: 'absolute',
      display: 'flex',
      top: theme.spacing(2),
      left: theme.spacing(2)
    },
    comparisonPositive: {
      color: green[300]
    },
    comparisonNegative: {
      color: red[300]
    }
  });

type IProps = WithStyles<typeof styles> & {
  title: string;
  description: React.ReactNode;
  value?: string | number;
  variant?: 'dollars' | 'percent';
  cbsaValue?: number;
};

const getPercentDifference = (v1: number, v2: number): number =>
  (v1 - v2) / Math.abs(v2);

const Statistic: React.FC<IProps> = ({
  classes,
  title,
  description,
  value,
  variant,
  cbsaValue
}) => {
  const percentDiff =
    typeof value === 'number' &&
    cbsaValue !== undefined &&
    getPercentDifference(value, cbsaValue);

  const isPositive =
    percentDiff !== false && (percentDiff === 0 || percentDiff > 0);

  const displayCbsaComparison = numeral(Math.abs(percentDiff || 0)).format(
    '0%'
  );

  const isArrowShowing =
    Number(value) >= 0 && Number(cbsaValue) >= 0 && percentDiff !== false;

  return value === undefined || Number(value) <= 0 ? (
    <></>
  ) : (
    <Grid item xs={12} md={4}>
      <Paper className={classes.root}>
        {isArrowShowing && (
          <div
            className={classnames(
              classes.comparison,
              isPositive
                ? classes.comparisonPositive
                : classes.comparisonNegative
            )}
          >
            {percentDiff === 0 ? (
              <></>
            ) : isPositive ? (
              <ArrowDropUpRounded fontSize="small" />
            ) : (
              <ArrowDropDownRounded fontSize="small" />
            )}
            <Typography align="right" variant="subtitle2">
              {displayCbsaComparison}
            </Typography>
          </div>
        )}
        <InfoBubble
          title={title}
          body={
            <>
              {description && (
                <Typography variant="caption" gutterBottom color="inherit">
                  {description}
                  <br />
                </Typography>
              )}

              {isArrowShowing && (
                <Typography variant="caption" color="inherit">
                  {`  The ${
                    isPositive ? 'green' : 'red'
                  } arrow to the left means that the ${title} for the Micromarket you're looking at is `}
                  <b>
                    {displayCbsaComparison}
                    {` `}
                    {isPositive ? `higher` : `lower`}
                  </b>
                  {' than usual for the city it is in.'}
                </Typography>
              )}
            </>
          }
        />
        <Typography align="center" className={classes.value} variant="h3">
          {!variant && value}
          {variant === 'dollars' && '$'}
          {typeof value === 'number' &&
            variant === 'percent' &&
            numeral(value).format('0%')}
          {typeof value === 'number' &&
            variant === 'dollars' &&
            (value > 999999
              ? numeral(value).format('0,0.0a') // over million, show 1.1m
              : value > 9999
              ? numeral(value).format('0,0a') // over 1000, show
              : numeral(value).format('0,0.0a'))}
        </Typography>
        <Typography
          align="center"
          className={classes.title}
          variant="subtitle2"
        >
          {title}
        </Typography>
      </Paper>
    </Grid>
  );
};

export default withStyles(styles)(Statistic);
